import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSearchClients, useUpdateHistorySearch } from 'modules/shell/hooks';
import { DropdownIcon, CloseIcon } from 'sharedComponents/Icons';
import { SEARCH_BY, SEARCH_LEVEL } from 'constants/index';
import { CircularProgress, Icon, IconButton, ListItem } from '@material-ui/core';
import CreateProfileButton from './components/CreateProfileButton';
import AddProfileForm from './components/AddProfileForm';
import MainHeader from '../MainHeader';
import PageLayout from '../PageLayout';
import Search from '../SearchRM';
import Button from '../Button';
import Tabs from '../Tabs';
import List from '../List';
import Drawer from '../Drawer';

const ClientSearchModal = ({
  classes,
  title,
  searchLabel,
  hasMore,
  isCentralSearch,
  searchCentralClients,
  clientList,
  recentSearchList,
  renderClientList,
  loading,
  goBack,
  withTabs,
  initSearch,
  titleWithSearch,
  rootElementRef,
  onLoadMore,
  searchPage,
  showHeaderCloseIcon,
  showAddProfileButton,
  withAddClientButton,
}) => {
  const history = useHistory();
  const searchParamsFromURL = new URLSearchParams(history.location.search);

  const [page, setPage] = React.useState(1);
  const [tabValue, setTabValue] = React.useState(searchParamsFromURL.get('searchLevel') || SEARCH_LEVEL.CLIENT_LIST);
  const [drawer, setDrawer] = React.useState(null);
  const [searchBy, setSearchBy] = React.useState(
    searchParamsFromURL.has('field') ? searchParamsFromURL.get('field') : SEARCH_BY[0],
  );

  const [drawerStack, drawerDispatch] = React.useReducer(Drawer.reducer, []);
  const activeDrawer = drawerStack[drawerStack.length - 1] || { content: () => null };

  const [search, setSearch] = React.useState(searchParamsFromURL.get('q') || initSearch || '');
  const isSearchLimitReached = search.length >= 2;
  const isSearch = React.useRef(!!search);
  const showRecentSearchResult = search.length === 0;
  const showTabs = withTabs && !showRecentSearchResult;
  const [, setQuery] = useUpdateHistorySearch({
    q: search,
    field: searchBy,
    searchLevel: tabValue,
  });
  useSearchClients(search, isSearch, isSearchLimitReached, tabValue, searchBy, withAddClientButton);

  React.useEffect(() => {
    setPage(1);
  }, [search]);

  React.useEffect(() => {
    setQuery({
      q: search,
      field: searchBy,
      searchLevel: tabValue,
    });
  }, [search, searchBy, tabValue]);

  const onLoadMoreHandler = () => {
    const newPage = searchPage ? searchPage + 1 : page + 1;
    searchCentralClients({
      search,
      field: searchBy,
      searchLevel: tabValue,
      page: newPage,
      withAddClientButton,
    });
    setPage(newPage);
    onLoadMore(newPage);
  };

  const tabs = [
    {
      label: 'client list',
      disabled: false,
    },
    {
      label: 'all',
      disabled: false,
    },
  ];

  const onDrawerClose = () => setDrawer(null);
  const onChangeSearchByHandler = () =>
    setDrawer({
      title: 'Search By',
      content: <SearchBy onClose={onDrawerClose} />,
    });

  const handleTabChange = (e, value) => {
    const tabValue = value ? SEARCH_LEVEL.GLOBAL : SEARCH_LEVEL.CLIENT_LIST;
    setTabValue(tabValue);
  };

  const handleSearchByChange = (value) => {
    setSearchBy(value);
    onDrawerClose();
  };

  const renderSearchResults = () => {
    if (loading) {
      return <CircularProgress data-testid="circular-progress" className={classes.loader} />;
    }

    if (showAddProfileButton) {
      return (
        <CreateProfileButton
          onClick={() =>
            drawerDispatch({
              type: 'open',
              payload: {
                title: 'New Client',
                content: () => <AddProfileForm searchTerm={search} searchBy={searchBy} history={history} />,
              },
            })
          }
        />
      );
    }

    if (!clientList.length && isSearchLimitReached && !loading && isSearch.current) {
      return (
        <div className={classes.empty} data-testid="no-records-found">
          No records found
        </div>
      );
    }

    return renderClientList(showRecentSearchResult ? recentSearchList : clientList, showRecentSearchResult);
  };

  const SearchBy = () => (
    <List className={classes.list}>
      {SEARCH_BY.map((value) => (
        <ListItem
          key={value}
          onClick={() => handleSearchByChange(value)}
          className={classes.item}
          data-testid="btn-search-by"
          aria-label="btn-search-by"
        >
          {value}
        </ListItem>
      ))}
    </List>
  );

  const renderTitle = () => {
    return (
      <div className={classes.title}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="go back"
          data-testid="go-back"
          onClick={goBack || history.goBack}
          classes={{ root: classes.icon }}
        >
          <Icon component={CloseIcon} fontSize="small" />
        </IconButton>
        <div>{title}</div>
      </div>
    );
  };

  const getCloseIcon = () => {
    if (showHeaderCloseIcon) return CloseIcon;
    if (title) return null;
    return 'back';
  };

  return (
    <>
      <MainHeader
        data-testid="main-header"
        title="Clients"
        closeIcon={getCloseIcon()}
        withTabs={showTabs}
        divider={!showTabs}
        classes={{ headerWithTabs: isCentralSearch ? classes.header : null }}
        titleWithSearch={titleWithSearch}
        customTitle={title ? renderTitle : null}
        onClose={goBack || history.goBack}
        search={() => {
          return (
            <>
              {searchLabel && <div className={classes.searchLabel}>{searchLabel}</div>}
              <Search
                placeholder="Search clients"
                search={search}
                applySearch={setSearch}
                withDebounce
                isHeaderWithin
                hasClear
                autoFocus
              />
              <Button
                classes={{ root: classes.btnSelect }}
                variant="outlined"
                endIcon={<DropdownIcon />}
                onClick={onChangeSearchByHandler}
                data-testid="open-search-by"
                aria-label="open-search-by"
              >
                {searchBy}
              </Button>
            </>
          );
        }}
      />
      <PageLayout
        classes={{ scrollAreaWithTabs: isCentralSearch ? classes.scrollArea : classes.fixPageLayoutForIOS }}
        withTabs={showTabs}
        ref={rootElementRef}
      >
        {showTabs && (
          <Tabs
            tabs={tabs}
            onChange={handleTabChange}
            value={tabValue === SEARCH_LEVEL.CLIENT_LIST ? 0 : 1}
            classes={{ appBarRootFixed: isCentralSearch ? classes.tabs : null }}
            isFixed
          />
        )}
        {renderSearchResults()}
        {hasMore && !showRecentSearchResult && (
          <Button
            className={classes.loadMore}
            variant="outlined"
            color="inherit"
            label="Load More"
            onClick={onLoadMoreHandler}
            data-testid="search-clients-load-more"
          />
        )}
        <Drawer title={drawer?.title} open={!!drawer} onClose={onDrawerClose} fullWidth={drawer?.fullWidth}>
          {drawer && drawer.content}
        </Drawer>
        <Drawer
          open={drawerStack.length > 0}
          title={activeDrawer.title}
          onClose={() => drawerDispatch({ type: 'closeAll' })}
          fullWidth
          closeIconFirst
          withoutDivider
        >
          {activeDrawer.content()}
        </Drawer>
      </PageLayout>
    </>
  );
};

ClientSearchModal.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  clientList: PropTypes.arrayOf(PropTypes.shape({})),
  recentSearchList: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
  searchLabel: PropTypes.string,
  isCentralSearch: PropTypes.bool,
  goBack: PropTypes.func,
  withTabs: PropTypes.bool,
  rootElementRef: PropTypes.shape({}),
  onLoadMore: PropTypes.func,
  searchPage: PropTypes.number,
  showHeaderCloseIcon: PropTypes.bool,
  withAddClientButton: PropTypes.bool,
};

ClientSearchModal.defaultProps = {
  classes: {},
  clientList: [],
  recentSearchList: [],
  title: '',
  searchLabel: '',
  isCentralSearch: false,
  goBack: null,
  withTabs: false,
  rootElementRef: null,
  onLoadMore: () => null,
};

export default ClientSearchModal;
